<template>
  <Modal
    v-if="isVisible"
    :show="isVisible"
    :title="t('Cidades de') + ' ' + estadoName"
    :close-label="t('Fechar')"
    elem-id="modalLocationCities"
    @close="close"
    @dismiss="close"
    >
      

    <div>
      <p>{{ t('Selecione as cidades para adicionar à franquia') }}:</p>

      <!-- Search filter -->
      <div class="form-group">
        <label for="searchCity">{{ t('Procurar') }}:</label>
        <input-text
          data-testid="franquias-locations-cities-search"
          elem-id="franquias_citiesSearchFilter"
          id="searchCity"
          v-model="searchFilter"
          :placeholder="t('Digite para filtrar')"
          style="width: 100%;" />
      </div>

      <!-- Loading state -->
      <inline-message v-if="isLoading" severity="info" :closable="false">
        {{ t('Carregando cidades') }}...
      </inline-message>

      <!-- Cidades list -->
      <table v-else class="table table-hover table-striped table-bordered" style="max-height: 400px; overflow-y: auto;">
        <tbody>
          <tr v-for="cidade in filteredCidades" :key="cidade.ID">
            <td style="width: 50%">
              <i v-if="isAlreadyAdded(cidade.ID)" class="pi pi-check"></i>
              {{ cidade.Name }}
            </td>
            <td style="width: 25%">
              <Button
                :data-testid="`franquias-locations-add-city-${cidade.ID}`"
                icon="pi pi-plus"
                :label="t('Adicionar cidade')"
                size="small"
                severity="secondary"
                @click="handleAddCity(cidade.ID)"
                :disabled="isAlreadyAdded(cidade.ID)" />
            </td>
            <td style="width: 25%">
              <Button
                :data-testid="`franquias-locations-see-neighborhoods-${cidade.ID}`"
                icon="pi pi-eye"
                :label="t('Ver bairros')"
                size="small"
                severity="info"
                @click="handleSeeNeighborhoods(cidade.ID, cidade.Name)" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalidades } from '../../composables/useLocalidades';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface CidadeItem {
  ID?: Id;
  Name?: string;
  [key: string]: unknown;
}

interface FranquiaData {
  AreasAtuacao?: {
    Cities?: Array<{ ID?: Id }>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface ModalOpenPayload {
  franquiaId?: Id;
  franquiaData?: FranquiaData;
  estadoId?: Id;
  estadoName?: string;
}

interface Props {
  franquiaId?: number | null;
  franquiaData?: FranquiaData | null;
}

const props = withDefaults(defineProps<Props>(), {
  franquiaId: null,
  franquiaData: null,
});

const { t } = useI18n();
const { fetchCidades, toggleRelacao } = useLocalidades();

// Reactive data
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const cidades = ref<CidadeItem[]>([]);
const searchFilter = ref<string>('');
const currentEstadoId = ref<Id | null>(null);
const estadoName = ref<string>('');
const currentFranquiaId = ref<Id | null>(null);
const currentFranquiaData = ref<FranquiaData | null>(null);

// Computed
const filteredCidades = computed<CidadeItem[]>(() => {
  if (!searchFilter.value) {
    return cidades.value;
  }
  const filter = searchFilter.value.toLowerCase();
  return cidades.value.filter((cidade) =>
    (cidade.Name || '').toLowerCase().includes(filter),
  );
});

const loadCidades = async (): Promise<void> => {
  if (!currentEstadoId.value) {
    return;
  }

  isLoading.value = true;
  try {
    cidades.value = (await fetchCidades(Number(currentEstadoId.value))) as CidadeItem[];
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao carregar cidades'));
  } finally {
    isLoading.value = false;
  }
};

// Methods
const open = async (estadoId: Id, estadoNameValue: string): Promise<void> => {
  isVisible.value = true;
  currentEstadoId.value = estadoId;
  estadoName.value = estadoNameValue;
  searchFilter.value = '';
  await loadCidades();
};

const close = (): void => {
  isVisible.value = false;
  searchFilter.value = '';
  cidades.value = [];
  currentEstadoId.value = null;
  estadoName.value = '';
};

// Event handlers for centralized ModalManager
const handleModalOpen = (data?: ModalOpenPayload): void => {
  if (data?.franquiaId) {
    currentFranquiaId.value = data.franquiaId;
  }
  if (data?.franquiaData) {
    currentFranquiaData.value = data.franquiaData;
  }
  if (data?.estadoId && data?.estadoName) {
    void open(data.estadoId, data.estadoName);
  }
};

const isAlreadyAdded = (cidadeId: Id | undefined): boolean => {
  const franquiaData = currentFranquiaData.value || props.franquiaData;
  if (!franquiaData || !franquiaData.AreasAtuacao) {
    return false;
  }
  const cities = franquiaData.AreasAtuacao.Cities || [];
  return cities.some((city) => city.ID === cidadeId);
};

const handleAddCity = async (cidadeId: Id | undefined): Promise<void> => {
  const franquiaId = currentFranquiaId.value || props.franquiaId;
  try {
    // NOTE: payload uses PascalCase keys preserved from the legacy JS code;
    // see FranquiasLocationsTab.vue for the documented latent contract mismatch.
    const relacaoData = {
      Type: 'city',
      LocalidadeID: cidadeId,
      OwnerID: franquiaId,
      OwnerType: 'franquia',
    };

    await toggleRelacao(relacaoData as unknown as Parameters<typeof toggleRelacao>[0]);

    eventBus.emit('globalMessage.success', t('Cidade adicionada com sucesso'));

    // Emit via eventBus so FranquiasLocationsTab can refresh permissions
    eventBus.emit('location-added', { franquiaId: franquiaId });
    close();
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao adicionar cidade'));
  }
};

const handleSeeNeighborhoods = (cidadeId: Id | undefined, cidadeName: string | undefined): void => {
  // Emit event via eventBus to open neighborhoods modal (centralized modal pattern)
  eventBus.emit('modal.locations-neighborhoods.open', {
    cidadeId: cidadeId,
    cidadeName: cidadeName,
    franquiaId: currentFranquiaId.value,
    franquiaData: currentFranquiaData.value,
  });

  // Close this modal
  close();
};

// Lifecycle hooks for centralized ModalManager
onMounted(() => {
  eventBus.on('modal.locations-cities.open', handleModalOpen);
  eventBus.on('modal.locations-cities.close', close);
});

onBeforeUnmount(() => {
  eventBus.off('modal.locations-cities.open', handleModalOpen);
  eventBus.off('modal.locations-cities.close', close);
});

// Expose methods for parent
defineExpose({
  open,
  close,
});
</script>

<style scoped>
.form-group {
  margin-bottom: 15px;
}

table {
  display: block;
  max-height: 400px;
  overflow-y: auto;
}

/* PrimeVue components handle their own styling */
</style>
